import { Injectable } from '@angular/core';
import { Headers, Response, URLSearchParams, RequestOptions, ResponseContentType } from '@angular/http';
import { AuthHttp } from 'angular2-jwt';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/forkJoin';
import { Observable } from 'rxjs/Observable';
import { Modules } from '../_models/modules';
import { DrilldownResponse, JSendResponse, ModuleFamily } from '../_models/drilldown';
import { Log } from 'ng2-logger';
import {ACLFeature} from '../_models/acl-feature';
import { Store, select } from '@ngrx/store';
import { selectImpersonatedUserId } from '../store/selectors/user.selectors';
import {UserState} from '../store/reducers/user.reducer';
import {switchMap} from 'rxjs/operators';
import {of} from 'rxjs/observable/of';
import 'rxjs/add/observable/throw';
import { ToastsManager } from 'ng2-toastr';
import * as Sentry from '@sentry/browser';

@Injectable()
export class AdminService {

  constructor(private authHttp: AuthHttp,
              private router: Router,
              private store: Store<{ user: UserState }>,
              private toasts: ToastsManager) {
  }

  getDriverGroups(): Observable<any> {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-groups/`)
        .map((response: Response) => response.json())
        .map((response: JSendResponse) => {
          return response.data;
        })
        .catch(error => this.customErrorHandler(error));
  }

  getCompanies(): Observable<any> {
    return this.authHttp.get(`${environment.API_ENDPOINT}/management/companies/`)
        .map((response: Response) => response.json())
        .map((response: JSendResponse) => {
          return response.data;
        })
        .catch(error => this.customErrorHandler(error));
  }

  getUsers(companyId: string): Observable<any> {
      return this.authHttp.get(`${environment.API_ENDPOINT}/management/companies/${companyId}/users`)
          .map((response: Response) => response.json())
          .map((response: JSendResponse) => {
              return response.data;
          })
          .catch(error => this.customErrorHandler(error));
  }

  impersonate(userId: string): Observable<any> {
      return this.authHttp.get(`${environment.API_ENDPOINT}/management/impersonate/${userId}`)
          .map((response: Response) => response.json())
          .map((response: JSendResponse) => {
              return response.data;
          })
          .catch(error => this.customErrorHandler(error));
  }

  deimpersonate(): Observable<any> {
      return this.authHttp.get(`${environment.API_ENDPOINT}/management/deimpersonate`)
          .map((response: Response) => response.json())
          .map((response: JSendResponse) => {
              return response.data;
          })
          .catch(error => this.customErrorHandler(error));
  }

  manageCompanyPortfolio(companyId): Observable<any> {
    return this.authHttp.get(`${environment.API_ENDPOINT}/management/manage-company-portfolio/${Number(companyId)}`)
      .map((response: Response) => response.json())
      .map((response: JSendResponse) => {
        return response.data;
      })
      .catch(error => this.customErrorHandler(error));
  }

  deAssumeRole(): Observable<any> {
    return this.authHttp.get(`${environment.API_ENDPOINT}/management/de-assume-role/`)
      .map((response: Response) => response.json())
      .map((response: JSendResponse) => {
        return response.data;
      })
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Check if current user is assuming a role other than his own role.
   * @returns {Observable<any>}
   */
  isAssumingRole(): Observable<any> {
    return this.authHttp.get(`${environment.API_ENDPOINT}/management/is-assuming-role/`)
      .map((response: Response) => response.json())
      .map((response: JSendResponse) => {
        return response.data;
      })
      .catch(error => this.customErrorHandler(error));
  }


  getAssumedCompany(): Observable<any> {
    return this.authHttp.get(`${environment.API_ENDPOINT}/management/get-assumed-company/`)
      .map((response: Response) => response.json())
      .map((response: JSendResponse) => {
        return response.data;
      })
      .catch(error => this.customErrorHandler(error));
  }

  isImpersonating(): Observable<any> {
    return this.store.pipe(select(selectImpersonatedUserId))
      .map((impersonatedUserId) => {
        return !!impersonatedUserId;
      });

    /*
    return this.authHttp.get(`${environment.API_ENDPOINT}/management/is-impersonating/`)
      .map((response: Response) => response.json())
      .map((response: JSendResponse) => {
        return response.data;
      })
      .catch(error => this.customErrorHandler(error));
    */
  }

  getImpersonatedUser(): Observable<any> {
    return this.store.pipe(
      select(selectImpersonatedUserId),
      switchMap((impersonatedUserId) => {
        console.log('impersonatedUserId', impersonatedUserId);

        if (!impersonatedUserId) {
          return of(null);
        }

        const headers = new Headers();
        headers.append('X-Impersonates', impersonatedUserId);

        return this.authHttp.get(`${environment.API_ENDPOINT}/management/get-impersonated-user/`, {
          headers: headers
        })
          .map((response: Response) => response.json())
          .map((response: JSendResponse) => {
            console.log('getImpersonatedUser', JSON.stringify(response.data));
            return response.data;
          })
          .catch(error => this.customErrorHandler(error));
      })
    );
  }

  /**
   * Get a family
   * @param type
   * @param id
   * @returns {Observable<ModuleFamily>}
   */
  getFamilies(type: string, id: number): Observable<any> {
    const path = (type === 'modules') ? 'module-families' : 'driver-families';
    return this.authHttp.get(`${environment.API_ENDPOINT}/${path}/${id}`)
      .map((response: Response) => response.json())
      .map((response: JSendResponse) => {
        return response.data as ModuleFamily;
      })
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Gets all possible capabilities for modules
   * @returns {Observable<any>}
   */
  getModuleCapabilities() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/module-capabilities/`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Gets all possible capabilities for drivers
   * @returns {Observable<any>}
   */
  getDriverCapabilities() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-capabilities/`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get a driver
   * @param id
   * @returns {Observable<any>}
   */
  getDriverController(id: number) {
    return this.authHttp.get(`${environment.API_ENDPOINT}/drivers/${id}`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }


  /**
   * Get multiple drivers
   * @param ids
   * @returns {Observable<any>}
   */
  getDriverControllers(ids: Array<any>) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/drivers/subset`, ids)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }


  /**
   * Post one or more drivers
   * @param data
   * @returns {Observable<any>}
   */
  postDriverControllers(data) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');
    const options = new RequestOptions({ headers: headers });

    return this.authHttp.post(`${environment.API_ENDPOINT}/drivers/`, data, options)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }


  /**
   * Post a driver
   * @param id
   * @param data
   * @returns {Observable<any>}
   */
  postDriverController(id: number, data) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/drivers/${id}`, data)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }


  /**
   * get a module
   * @param id
   * @returns {Observable<any>}
   */
  getModuleController(id: number) {
    return this.authHttp.get(`${environment.API_ENDPOINT}/modules/${id}`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get multiple modules
   * @param ids
   * @returns {Observable<any>}
   */
  getModuleControllers(ids: Array<any>) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/modules/subset`, ids)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }


  /**
   * Post a module
   * @param id
   * @returns {Observable<any>}
   */
  postModuleController(id: number, data) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/modules/${id}`, data)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Post one or more modules
   * @param data
   * @returns {Observable<any>}
   */
  postModuleControllers(data) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');
    const options = new RequestOptions({ headers: headers });

    return this.authHttp.post(`${environment.API_ENDPOINT}/modules/`, data, options)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get a bridge
   * @param id
   * @returns {Observable<any>}
   */
  getBridgeController(id: number) {
    return this.authHttp.get(`${environment.API_ENDPOINT}/bridges/${id}`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get multiple bridges
   * @param ids
   * @returns {Observable<any>}
   */
  getBridgeControllers(ids: Array<any>) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/bridges/subset`, ids)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Post a bridge
   * @param id
   * @param data
   * @returns {Observable<any>}
   */
  postBridgeController(id: number, data) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/bridges/${id}`, data)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Post one or more bridges
   * @param data
   * @returns {Observable<any>}
   */
  postBridgeControllers(data) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');
    const options = new RequestOptions({ headers: headers });

    return this.authHttp.post(`${environment.API_ENDPOINT}/bridges/`, data, options)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get a sensor
   * @param id
   * @returns {Observable<any>}
   */
  getSensorController(id: number) {
    return this.authHttp.get(`${environment.API_ENDPOINT}/sensors/${id}`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get multiple sensors
   * @param ids
   * @returns {Observable<any>}
   */
  getSensorControllers(ids: Array<any>) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/sensors/subset`, ids)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Post a sensor
   * @param id
   * @param data
   * @returns {Observable<any>}
   */
  postSensorController(id: number, data) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/sensors/${id}`, data)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Post one or more sensors
   * @param data
   * @returns {Observable<any>}
   */
  postSensorControllers(data) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');
    const options = new RequestOptions({ headers: headers });

    return this.authHttp.post(`${environment.API_ENDPOINT}/sensors/`, data, options)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  getSensorFeatures() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/sensor-features/`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  getSensorApplications() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/sensor-applications/`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  getSensorTechnologies() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/sensor-technologies/`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  getSensorAccessoryTypes() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/sensor-accessory-types/`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get a sensor accessory
   * @param id
   * @returns {Observable<any>}
   */
  getSensorAccessoryController(id: number) {
    return this.authHttp.get(`${environment.API_ENDPOINT}/sensor-accessory/${id}`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get multiple sensor accessories
   * @param ids
   * @returns {Observable<any>}
   */
  getSensorAccessoryControllers(ids: Array<any>) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/sensor-accessory/subset`, ids)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Post a sensor accessory
   * @param id
   * @param data
   * @returns {Observable<any>}
   */
  postSensorAccessoryController(id: number, data) {
    return this.authHttp.post(`${environment.API_ENDPOINT}/sensor-accessory/${id}`, data)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Post one or more sensor accessories
   * @param data
   * @returns {Observable<any>}
   */
  postSensorAccessoryControllers(data) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');
    const options = new RequestOptions({ headers: headers });

    return this.authHttp.post(`${environment.API_ENDPOINT}/sensor-accessory/`, data, options)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all possible dimming type controllers
   * @returns {Observable<any>}
   */
  getDimmingTypes() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-dimming-types/`)
        .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all possible mounting types
   * @returns {Observable<any>}
   */
  getMountingTypes() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-mounting-types/`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all possible certifications
   * @returns {Observable<any>}
   */
  getCertifications() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-certifications/`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all possible features
   * @returns {Observable<any>}
   */
  getFeatures() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-features/`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all input voltages
   * @returns {Observable<any>}
   */
  getInputVoltages() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-input-voltages/`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all isolation classes
   * @returns {Observable<any>}
   */
  getIsolationClasses() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-isolation-classes/`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all driver classifications
   * @returns {Observable<any>}
   */
  getDriverClassifications() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-classifications/`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all voltage types
   * @returns {Observable<any>}
   */
  getVoltageTypes() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-voltage-types/`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all driver families
   * @returns {Observable<any>}
   */
  getDriverFamilies() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/driver-families/`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  // get module tree
  getModuleTree(region): Observable<any> {
    let apiString;

    if (region) {
      apiString = `${environment.API_ENDPOINT}/management/whitelist/module-tree?region=${region}`;
    } else {
      apiString = `${environment.API_ENDPOINT}/management/whitelist/module-tree`;
    }

    return this.authHttp.get(apiString)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  getDriversTree(region): Observable<any> {
    let apiString;

    if (region) {
      apiString = `${environment.API_ENDPOINT}/management/whitelist/driver-tree?region=${region}`;
    } else {
      apiString = `${environment.API_ENDPOINT}/management/whitelist/driver-tree`;
    }

    return this.authHttp.get(apiString)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  getSensorDriversTree(region): Observable<any> {
    let apiString;

    if (region) {
      apiString = `${environment.API_ENDPOINT}/management/whitelist/sensor-driver-tree?region=${region}`;
    } else {
      apiString = `${environment.API_ENDPOINT}/management/whitelist/sensor-driver-tree`;
    }

    return this.authHttp.get(apiString)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  getWhitelistExport(driverIds, moduleIds): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('driverIds', driverIds);
    formData.append('moduleIds', moduleIds);

    const url = `${environment.API_ENDPOINT}/management/whitelist/export`;
    return this.authHttp.post(url, formData, { responseType: ResponseContentType.Blob })
      .map((response) => {
        return new Blob([response.blob()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      })
      .catch(error => this.customErrorHandler(error));
  }

  getSensorWhitelistExport(sensorDriverIds, region, allCompanies): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('driverIds', sensorDriverIds);
    formData.append('region', region);
    formData.append('allCompanies', allCompanies);

    const url = `${environment.API_ENDPOINT}/management/whitelist/sensor/export`;
    return this.authHttp.post(url, formData, { responseType: ResponseContentType.Blob })
        .map((response) => {
          return new Blob([response.blob()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        })
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all module families
   * @returns {Observable<any>}
   */
  getModuleFamilies() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/module-families/`)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get module family by id
   * @returns {Observable<any>}
   */
  getModuleFamily(id) {
    return this.authHttp.get(`${environment.API_ENDPOINT}/module-families/${id}`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  getFamily(type: string, id: number) {
    const path = (type === 'modules') ? 'module-families' : 'driver-families';
    return this.authHttp.get(`${environment.API_ENDPOINT}${'/' + path}${'/' + id}`)
        .map((response: Response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get all voltageTypes
   * @returns {Observable<any>}
   */
  getModuleVoltageTypes() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/module-voltage-types/`)
        .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Delete a driver | module family
   * @param id
   * @returns {Observable<any>}
   */
  deleteFamilies(type: string, id: number): Observable<any> {
    const path = (type === 'modules') ? 'module-families' : 'driver-families';
    const familyId = id.toString();
    return this.authHttp.delete(`${environment.API_ENDPOINT}${'/' + path}${'/' + familyId}`)
      .map((response: Response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Delete batch of modules or drivers
   * @param type
   * @param id
   * @returns {Observable<any>}
   */
  deleteBatch(type: string, ids: Array<number>): Observable<any> {
    const path = (type === 'modules') ? 'modules' : 'drivers';

    return Observable.forkJoin(ids.map((id) => {
      return this.authHttp.delete(`${environment.API_ENDPOINT}${'/' + path}${'/' + id}`)
          .map((response: Response) => response.json())
          .catch(error => Observable.throw(error));
    }));
  }

  /**
   * Post an image
   * @param data
   * @returns {Observable<any>}
   */
  postImage(data): Observable<any> {
    const fileList: FileList = data.target.files;
    if (fileList.length > 0) {
      const file: File = fileList[0];

      const formData: FormData = new FormData();
      formData.append('uploadfile', file, file.name);
      // console.log(formData.get('uploadfile'));
      const headers = new Headers();
      headers.append('Accept', 'application/json');

      const options = new RequestOptions({ headers: headers });

      return this.authHttp.post(`${environment.API_ENDPOINT}/image`, formData, options)
        .map((response) => response.json())
        // .catch(error => Observable.throw(error))
        .catch(error => this.customErrorHandler(error));
    }
  }

  /**
   * Upload CSV for type (driver/module) to add new products
   * @param file
   * @param productType The type of the product: 'module' or 'driver'
   * @param ignore_unknown_products Tells the system whether to create products for unknown EOCs found
   * @param driverType The type of the driver
   * @returns {Observable<any>}
   */
  postCSVFile(file: File, productType: string, driverType: string, ignore_unknown_products: boolean): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('uploadfile', file, file.name);
    formData.append('ignore_unknown_products', ignore_unknown_products.toString());
    formData.append('type_group', productType);
    formData.append('driver_group', driverType);

    return this.authHttp.post(`${environment.API_ENDPOINT}/management/import/file`, formData)
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Upload excel file for whitelisting (driver/module)
   * @param file
   * @returns {Observable<R>}
   */
  postExcelFile(file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('uploadfile', file, file.name);

    return this.authHttp.post(`${environment.API_ENDPOINT}/management/whitelist/import`, formData)
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Upload excel file for sensor whitelisting (driver/module)
   * @param file
   * @returns {Observable<R>}
   */
  postSensorExcelFile(file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('uploadfile', file, file.name);

    return this.authHttp.post(`${environment.API_ENDPOINT}/management/whitelist/sensor/import`, formData)
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Download CSV template (driver/module)
   * @param productType The type of the product: 'module' or 'driver'
   * @returns {Observable<any>}
   */
  downloadCSVTemplate(productType: string): Observable<any> {
    const url = `${environment.API_ENDPOINT}/management/download/${productType}-csv-template`;
    return this.authHttp.get(url, { responseType: ResponseContentType.Blob })
      .map((res) => {
        return new Blob([res.blob()], { type: 'text/csv' });
      })
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Download the full catalog of products for a certain productType and region
   * @param region The region to query for
   * @param productType The type of the product: 'module' or 'driver'
   * @param driverType
   * @param maintenanceMode
   * @returns {Observable<any>}
   */
  downloadCatalog(region: string, productType: string, driverType: string, maintenanceMode: boolean): Observable<any> {
    let url = `${environment.API_ENDPOINT}/management/download/export/${productType}-catalog/`;
    if (productType === 'driver') {
      url = url + `${driverType}/`;
    }
    url = url + `${region}`;
    url = url + `?mm=${maintenanceMode}`;

    return this.authHttp.get(url, { responseType: ResponseContentType.Blob })
        .map((res) => {
          return new Blob([res.blob()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        })
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * Fetch user catalog as blob
   * @returns {Observable<any>}
   */
  downloadUserCatalog(): Observable<any> {
    return this.authHttp.get(`${environment.API_ENDPOINT}/management/users`, { responseType: ResponseContentType.Blob })
      .map((res) => {
        return new Blob([res.blob()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      })
      .catch(error => this.customErrorHandler(error));
  }

    /**
     * Fetch role overview as blob
     * @returns {Observable<any>}
     */
    downloadRoleOverview(): Observable<any> {
        return this.authHttp.get(`${environment.API_ENDPOINT}/management/acl-features/excel`, { responseType: ResponseContentType.Blob })
            .map((res) => {
                return new Blob([res.blob()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            })
            .catch(error => this.customErrorHandler(error));
    }

  /**
   * Update a driver | module family
   * @param type
   * @param id
   * @param entity
   * @returns {Observable<any>}
   */
  updateFamilies(type: string, id: number, entity: string): Observable<any> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    const path = (type === 'modules') ? 'module-families' : 'driver-families';
    const familyId = id.toString();
    const data = entity;
    return this.authHttp.post(`${environment.API_ENDPOINT}/${path}/${familyId}`, data, {headers: headers})
        .map((response: Response) => response.json())
        .catch(error => this.customErrorHandler(error));

  }

  /**
   * submit a new family
   */
  newFamily(type: string, entity: string): Observable<any> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    const path = (type === 'modules') ? 'module-families' : 'driver-families';
    const data = entity;
    return this.authHttp.post(`${environment.API_ENDPOINT}/${path}/`, data, {headers: headers})
      .map((response: Response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }


  /**
   * Do drilldown API call
   * @param type
   * @param parentId
   * @param showArchived
   * @returns {Observable<any>}
   */
  drilldown(type?: string, parentId?: number, showArchived: boolean = false): Observable<DrilldownResponse> {
    const parameters = new URLSearchParams();
    if (parentId != null) {
      parameters.set('parentId', parentId.toString());
    }

    let path = '';

    switch (type) {
      case 'modules':
        path = (`${environment.API_ENDPOINT}`) + '/modules-drilldown';
        break;
      case 'drivers':
        path = (`${environment.API_ENDPOINT}`) + '/drivers-drilldown';
        break;
      case 'bridges':
        path = (`${environment.API_ENDPOINT}`) + '/bridges-drilldown';
        break;
      case 'sensors':
        path = (`${environment.API_ENDPOINT}`) + '/sensors-drilldown';
        break;
      case 'sensor-accessories':
        path = (`${environment.API_ENDPOINT}`) + '/sensor-accessory-drilldown';
        break;
    }

    if (showArchived) {
      parameters.set('showArchived', 'true');
    }

    return this.authHttp.get(`${path}`, {
        search: parameters
      })
      .map((response: Response) => response.json())
      .map((response: JSendResponse) => {
        return response.data as DrilldownResponse;
      })
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Get modules
   * @returns {Observable<any>}
   */
  modules(): Observable<Modules> {
    return this.authHttp.get(`${environment.API_ENDPOINT}/modules`)
      .map((response: Response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Checks if currently logged in user has access to a certain feature
   * @param feature ACLFeature
   * @returns {Observable<R|T>} comtaining "true" or "false" in data attribute
   */
  canI(feature: ACLFeature) {
    return this.authHttp.get(`${environment.API_ENDPOINT}/management/canI/${feature}`)
        .map((response: Response) => response.json())
        .map((response: JSendResponse) => {
          return response.data;
        })
        .catch(error => this.customErrorHandler(error));
  }

  /**
   * get acl features for current user
   * @param feature
   * @returns {Observable<R|T>}
   */
  aclFeatures(): Observable<Array<string>> {
    return this.authHttp.get(new URL('/api/v2/users/profile/acl-features', environment.BASE_URL).href)
      .map((response: Response) => response.json())
      .map((response: JSendResponse) => {
        return response.data.aclFeatures;
      })
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Custom error handler to check if the session is expired
   * @returns {Observable<any>}
   */
  customErrorHandler(errorObj): Observable<any> {
    const log = Log.create('customErrorHandler');

    log.error('There was an error', errorObj);
    Sentry.captureException(errorObj);

    if (errorObj.status === 422 || errorObj.status === 403) {
      log.error('the error was a 422 or 403, so we shall logout');
      this.router.navigate(['logout']);
    } else if (errorObj.status === 401) {
      log.error('the error was a 401, so we shall logout');
      this.router.navigate(['unauthorized']);
    } else {
      this.toasts.error(`Internal error ${errorObj.Status}`);
    }

    return Observable.throw(errorObj);
  }

  genericSearch(query): Observable<any> {
    return this.authHttp.get(`${environment.BASE_URL}/api/v1/search?searchTerm=${query}`, {})
      .map((response: Response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  getTagFamiliesController() {
    return this.authHttp.get(`${environment.API_ENDPOINT_V2}/tag-families/`)
        .map((response: Response) => response.json())
        .map((response: JSendResponse) => {
          return response.data;
        })
        .catch(error => this.customErrorHandler(error));
  }

  getTagFamilyController(id: number) {
    return this.authHttp.get(`${environment.API_ENDPOINT_V2}/tag-families/${id}`)
        .map((response) => response.json())
        .map((response: JSendResponse) => {
          return response.data;
        })
        .catch(error => this.customErrorHandler(error));
  }

  getTagController(id: number) {
    return this.authHttp.get(`${environment.API_ENDPOINT_V2}/tags/${id}`)
        .map((response) => response.json())
        .map((response: JSendResponse) => {
          return response.data;
        })
        .catch(error => this.customErrorHandler(error));
  }

  postTagController(data) {
    return this.authHttp.post(`${environment.API_ENDPOINT_V2}/tags/`, data)
        .map((response) => response.json())
        .catch(error => this.customErrorHandler(error));
  }

  deleteTagController(id: number) {
    return this.authHttp.delete(`${environment.API_ENDPOINT_V2}/tags/${id}`)
      .map((response) => response.json())
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Download excel file for family labels
   * @returns {Observable<R>}
   */
  familyLabelExport(moduleFamilyIds, driverFamilyIds): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('moduleFamilyIds', moduleFamilyIds);
    formData.append('driverFamilyIds', driverFamilyIds);

    const url = `${environment.API_ENDPOINT}/management/labeling/export`;
    return this.authHttp.post(url, formData, { responseType: ResponseContentType.Blob })
      .map((response) => {
        return new Blob([response.blob()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      })
      .catch(error => this.customErrorHandler(error));
  }

  /**
   * Upload excel file for family labels
   * @param file
   * @returns {Observable<R>}
   */
  familyLabelImport(file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('uploadfile', file, file.name);

    return this.authHttp.post(`${environment.API_ENDPOINT}/management/labeling/import`, formData)
      .catch(error => this.customErrorHandler(error));
  }

  indexElasticsearch(company: string, region: string) {
    return this.authHttp.get(`${environment.API_ENDPOINT}/es/${company}/${region}/import`)
      .catch(error => this.customErrorHandler(error));
  }

  checkElastisearchIndexStatus() {
    return this.authHttp.get(`${environment.API_ENDPOINT}/es/status`)
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/admin.service.ts